import * as React from 'react';
import arial from '../../style/Arial/arial.ttf';
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { IProjectMacro2 } from '../../Macro2/store/project/projectslice';
import { IProjectInfoForm } from '../../pages/ProjectInfo/IProjectInfo';
import PDFHeader from './PDFLayout/PDFHeader';
import { styles } from './PDFStyle';
import ProjectInformation from './PDFComponents/ProjectInformation';
import PDFFooter from './PDFLayout/PDFFooter';
import { IProjectMacro1 } from '../../Macro1/store/project/projectslice';
import { conversionLabel } from '../../conversion';
import PDFTable from './PDFTable/PDFTable';
import checkImage from '../../images/check.jpg';
import crossImage from '../../images/x-mark.jpg';
import { useEffect } from 'react';
import { getProjectSiteEnvLevelAsync } from '../../api/store/BeMaccaferriStoreHelper';

interface IPDFProps {
    project: IProjectMacro2;
    projectInfo: IProjectInfoForm;
    results: any;
}

Font.register({
    family: 'Arial',
    fonts: [
        {
            src: arial,
        },
    ],
});

const PDF: React.FunctionComponent<IPDFProps> = props => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;
    const [selectedSiteEnvLevel, setSelectedSiteEnvLevel] = React.useState<any>(null);
    //console.log('projectInfo', projectInfo);
    //console.log('project', project);
    //console.log('results', results);
    const imgSrc = localStorage.getItem('graphic') ?? '';

    let component: JSX.Element;

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.unit == 'Metric' ? 0 : 1] + ']';
    };

    useEffect(() => {
        const fetchSiteEnvLevel = async () => {
            try {
                const response = await getProjectSiteEnvLevelAsync(window.localStorage.getItem("i18nextLng") ?? 'en-US');
                const siteEnvLevel = response.data.find((el) => el.id === project.siteEnvironmentalLevelId);
                setSelectedSiteEnvLevel(siteEnvLevel);
            } catch (error) {
                console.error('Error fetching site environmental level:', error);
            }
        };

        fetchSiteEnvLevel();
    }, [project.siteEnvironmentalLevelId, window.localStorage.getItem("i18nextLng")]);

    const rockSlopeData = [
        [t('rock_slope_macro2_slope_inclination', { unit_measure: '' }), t('rock_slope_macro2_slope_inclination_symbol'), project.rockSlope ? project.rockSlope.inclination.toString() : '', getMeasurementUnit(conversionLabel.deg)],
        [t('rock_slope_macro2_slope_total_height', { unit_measure: '' }), t('rock_slope_macro2_slope_total_height_symbol'), project.rockSlope ? project.rockSlope.slopeTotalHeight.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('rock_slope_macro2_debris_accumulation_height', { unit_measure: '' }), t('rock_slope_macro2_debris_accumulation_height_symbol'), project.rockSlope ? project.rockSlope.debrisAccumulationHeight.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('rock_slope_macro2_debris_accumulation_width', { unit_measure: '' }), t('rock_slope_macro2_debris_accumulation_width_symbol'), project.rockSlope ? project.rockSlope.debrisAccumulationWidth.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('rock_slope_macro2_debris_accumulation_angle', { unit_measure: '' }), t('rock_slope_macro2_debris_accumulation_angle_symbol'), project.rockSlope ? project.rockSlope.debrisAccumulationAngle.toString() : '', getMeasurementUnit(conversionLabel.deg)],
        [t('rock_slope_macro2_debris_friction_angle', { unit_measure: '' }), t('rock_slope_macro2_debris_friction_angle_symbol'), project.rockSlope ? project.rockSlope.debrisFrictionAngle.toString() : '', getMeasurementUnit(conversionLabel.deg)],
        [t('rock_slope_macro2_debris_unit_weight', { unit_measure: '' }), t('rock_slope_macro2_debris_unit_weight_symbol'), project.rockSlope ? project.rockSlope.debrisUnitWeight.toString() : '', getMeasurementUnit(conversionLabel.kNm3_lbfft3)],
        [t('rock_slope_macro2_mesh_slope_friction_angle', { unit_measure: '' }), t('rock_slope_macro2_mesh_slope_friction_angle_symbol'), project.rockSlope ? project.rockSlope.meshSlopeFrictionAngle.toString() : '', getMeasurementUnit(conversionLabel.deg)],
    ];

    if (project.rockSlope?.snow) {
        rockSlopeData.push(
            [t('rock_slope_macro2_snow_thickness', { unit_measure: '' }), t('rock_slope_macro2_snow_thickness_symbol'), project.rockSlope.snowThickness.toString(), getMeasurementUnit(conversionLabel.m_ft)],
            [t('rock_slope_macro2_snow_unit_weight', { unit_measure: '' }), t('rock_slope_macro2_snow_unit_weight_symbol'), project.rockSlope.snowUnitWeight.toString(), getMeasurementUnit(conversionLabel.kNm3_lbfft3)]
        );
    }

    const siteEnvLevelData = [
        [t("site_environmental_level", { unit_measure: '' }), selectedSiteEnvLevel?.name || ''],
        [t('site_environmental_level_coating', { unit_measure: '' }), selectedSiteEnvLevel?.coatings.join('\n') || ''],
    ];

    const meshData = [
        [t('mesh_macro2_type'), project.mesh ? project.mesh.meshTypeName : '', ''],
        [t('mesh_macro2_tensile_resistence', { unit_measure: '' }), project.mesh ? project.mesh.tensileResistance.toString() : '', getMeasurementUnit(conversionLabel.kNm_lbfft)],
        [t('mesh_macro2_steel_unit_weight', { unit_measure: '' }), project.mesh ? project.mesh.steelMeshUnitWeight.toString() : '', getMeasurementUnit(conversionLabel.kgm2_lbfft2)],
    ];

    const crestData = [
        [t('crest_horizontal_spacing', { unit_measure: '' }), t('crest_symbols.crest_horizontal_spacing'), project.crest ? project.crest.horizontalAnchorSpacing.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
        [t('crest_rope_diameter', { unit_measure: '' }), t('crest_symbols.crest_rope_diameter'), project.crest ? project.crest.ropeDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('crest_rope_steel_grade', { unit_measure: '' }), t('crest_symbols.crest_rope_steel_grade'), project.crest ? project.crest.ropeSteelGrade.toString() : '', getMeasurementUnit(conversionLabel.mpa)],
        [t('crest_rope_core_type'), '', project.crest ? project.crest.ropeCoreTypeName : '', ''],
        [t('crest_rope_tensile_strength', { unit_measure: '' }), t('crest_symbols.crest_rope_tensile_strength'), project.crest ? project.crest.ropeUltimateTensileStrength.toString() : '', getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('crest_anchor_type'), '', project.crest ? project.crest.anchorTypeName : '', ''],
        [t('crest_inner_diameter', { unit_measure: '' }), t('crest_symbols.crest_inner_diameter'), project.crest ? project.crest.anchorInternalDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('crest_external_diameter', { unit_measure: '' }), t('crest_symbols.crest_external_diameter'), project.crest ? project.crest.anchorExternalDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('crest_drilling_diameter', { unit_measure: '' }), t('crest_symbols.crest_drilling_diameter'), project.crest ? project.crest.drillingDiameter.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        //[t('crest_automatic_thickness', { unit_measure: '' }), '', project.crest ? project.crest.automaticCrownThickness.toString() : '', ''],
        [t('crest_crown_thickness', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_custom_thickness'), project.crest ? project.crest.crownThickness.toString() : '', getMeasurementUnit(conversionLabel.mm_in)],
        [t('crest_anchor_yield_stress', { unit_measure: '' }), t('crest_symbols.crest_anchor_yield_stress'), project.crest ? project.crest.anchoryieldStress.toString() : '', getMeasurementUnit(conversionLabel.mpa_ksi)],
        //[t('crest_anchor_NFP94270'), '', project.crest ? project.crest.NFP94270.toString() : '', ''],
        [t('crest_rock_grout_adhesion', { unit_measure: '' }), t('crest_symbols.crest_rock_grout_adhesion'), project.crest ? project.crest.rockGroutAdhesion.toString() : '', getMeasurementUnit(conversionLabel.mpa_psi)],
        [t('crest_assumed_plasticization', { unit_measure: '' }), t('anchor_bar_symbols.anchor_bar_assumed_plasticization'), project.crest ? project.crest.assumedPlasticizationBetweenRockAndAnchor.toString() : '', getMeasurementUnit(conversionLabel.m_ft)],
    ];

    if (project.crest?.automaticCrownThickness) {
        crestData.push(
            [t('crest_environmental_aggressivity', { unit_measure: '' }), '', project.crest ? project.crest.environmentalAggressivity.toString() : '', getMeasurementUnit(conversionLabel.mmy_iny)],
            [t('crest_lifespan', { unit_measure: '' }), '', project.crest ? project.crest.lifespanYears.toString() : '', getMeasurementUnit(conversionLabel.years)]
        );
    }

    const safetyCoefficientsData = [
        [t('safety_macro2_variable_loads', { unit_measure: '' }), t('safety_symbols.safety_macro2_variable_loads'), project.safetyCoefficients ? project.safetyCoefficients.coefficientForVariableLoads.toString() : ''],
        [t('safety_macro2_permanent_loads', { unit_measure: '' }), t('safety_symbols.safety_macro2_permanent_loads'), project.safetyCoefficients ? project.safetyCoefficients.coefficientForPermanentLoads.toString() : ''],
        [t('safety_macro2_mesh_resistance_reduction', { unit_measure: '' }), t('safety_symbols.safety_macro2_mesh_resistance_reduction'), project.safetyCoefficients ? project.safetyCoefficients.reductionForMeshResistance.toString() : ''],
        [t('safety_macro2_x_spacing_reduction', { unit_measure: '' }), t('safety_symbols.safety_macro2_x_spacing_reduction'), project.safetyCoefficients ? project.safetyCoefficients.reductionOnXSpacing.toString() : ''],
        /* [t('safety_macro2_y_spacing_reduction', { unit_measure: '' }), t('safety_symbols.safety_macro2_y_spacing_reduction'), project.safetyCoefficients ? project.safetyCoefficients.reductionOnYGradient.toString() : ''], */
        [t('safety_macro2_cable_resistance_reduction', { unit_measure: '' }), t('safety_symbols.safety_macro2_cable_resistance_reduction'), project.safetyCoefficients ? project.safetyCoefficients.reductionForCableResistance.toString() : ''],
        [t('safety_macro2_steel_bar_yield_stress', { unit_measure: '' }), t('safety_symbols.safety_macro2_steel_bar_yield_stress'), project.safetyCoefficients ? project.safetyCoefficients.coefficientSteelBarYield.toString() : ''],
        [t('safety_macro2_rock_grout_adhesion', { unit_measure: '' }), t('safety_symbols.safety_macro2_rock_grout_adhesion'), project.safetyCoefficients ? project.safetyCoefficients.coefficientRockGroutAdesion.toString() : ''],
    ];

    const meshDesignData = [
        [t('general_results_design_total_stress', { unit_measure: '' }), results?.outputMeshTotalStressRevetment.designTotalStress.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
        [t('general_results_admissible_tensile_resistance', { unit_measure: '' }), results?.outputMeshTotalStressRevetment.tensileStrengthOfTheMesh.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
        [t('general_results_ratio_strength_stress', { unit_measure: '' }), results?.outputMeshSystemCheck.strengthForceRatio.toString(), ''],
        [t('general_results_debris_total_load', { unit_measure: '' }), results?.outputMeshResultantLoads.debrisTotalLoad.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
        [t('general_results_snow_total_load', { unit_measure: '' }), results?.outputMeshResultantLoads.snowTotalLoad.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
        [t('general_results_mesh_total_load', { unit_measure: '' }), results?.outputMeshResultantLoads.meshTotalLoad.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
        [t('general_results_resultant_stress_drapery', { unit_measure: '' }), results?.outputMeshResultantLoads.resultantStressOnTheRevetment.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
    ];

    const meshChecks = [
        [t('general_results_ratio_strength_stress'), results?.outputMeshSystemCheck.designCheckOfTheMesh === 'Satisfied' ],
    ];

    const cableDesignData = [
        [t('general_results_maximum_tensile_force_cable', { unit_measure: '' }), results?.outputTopCable.maximumTensileStressInTheCable.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_cable_working_load_nominal', { unit_measure: '' }), results?.outputTopCable.cableWorkingLoadLimitNominal.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_cable_load_force_ratio', { unit_measure: '' }), results?.outputTopCable.cableStrengthForceRatio.toString(), ''],
        [t('general_results_maximum_force_intermediate_anchorages', { unit_measure: '' }), results?.outputTopCable.maximumForceOnTheIntermediateAnchorages.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_maximum_force_lateral_anchorages', { unit_measure: '' }), results?.outputTopCable.maximumForceOnTheLateralAnchorages.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_maximum_admitted_distance_anchorages', { unit_measure: '' }), results?.outputTopCable.maximumAdmittedDistanceBetweenAnchorages.toString(), getMeasurementUnit(conversionLabel.m_ft)],
        [t('general_results_length_rope_total_anchorages', { unit_measure: '' }), results?.outputTopCable.lengthOfTheRopeTotalBetweenAnchorages.toString(), getMeasurementUnit(conversionLabel.m_ft)],
        [t('general_results_maximum_sag_adjacent_anchorages', { unit_measure: '' }), results?.outputTopCable.maximumSagBetweenAdjacentAnchorages.toString(), getMeasurementUnit(conversionLabel.m_ft)],
        //20250120 don't show it [t('general_results_cable_maximum_debris_weight', { unit_measure: '' }), results?.outputMaximumDebrisLoad.maximumAdmissibleDebrisWeight.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
    ];

    const cableChecks = [
        [t('general_results_cable_load_force_ratio'), results?.outputTopCable.designCheckOfTheCable === 'Satisfied' ],
    ];

    const topAnchoragesDesignData = [
        [t('general_results_working_section_steel', { unit_measure: '' }), results?.outputTopAnchorages.effectiveCrossSectionalAreaOfTheAnchoages.toString(), getMeasurementUnit(conversionLabel.mm2_in2)],
        [t('general_results_working_tensile_resistance_anchorages_nominal', { unit_measure: '' }), results?.outputTopAnchorages.workingTensileResistanceOfTheAnchoragesNomunal.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_minimum_required_tensile_strength_cable_anchors', { unit_measure: '' }), results?.outputTopAnchorages.minimumRequiredTensileStrengthOfCableAnchors.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_minimum_drilling_diameter', { unit_measure: '' }), results?.outputTopAnchorages.minimumDrillingDiameterNominal.toString(), getMeasurementUnit(conversionLabel.mm_in)],
        [t('general_results_minimum_length_anchor', { unit_measure: '' }), results?.outputTopAnchorages.minimumTheoreticalTotalCableAnchorLengthNominal.toString(), getMeasurementUnit(conversionLabel.m_ft)],
        //20250120 don't show it [t('general_results_anchors_maximum_debris_weight', { unit_measure: '' }), results?.outputMaximumDebrisLoad.maximumDebrisWeightAnchors.toString(), getMeasurementUnit(conversionLabel.kNm_lbfft)],
    ];

    const lateralAnchoragesDesignData = [
        [t('general_results_maximum_force_lateral_anchorages', { unit_measure: '' }), results?.outputTopAnchorages.forceOnTheLateralAnchorages.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_working_shear_resistance_anchorages_nominal', { unit_measure: '' }), results?.outputTopAnchorages.workingShearResistanceOfTheAnchoragesNominal.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_shear_resistance_ratio', { unit_measure: '' }), results?.outputTopAnchorages.calculationsForTheLateralAnchorages.toString(), ''],
    ];

    const lateralChecks = [
        [t('general_results_shear_resistance_ratio'), results?.outputTopAnchorages.designCheckOfTheLateralAnchoragesBars === 'Satisfied' ],
    ];

    const intermediateAnchoragesDesignData = [
        [t('general_results_maximum_force_intermediate_anchorages', { unit_measure: '' }), results?.outputTopAnchorages.forceOnTheIntermediateAnchorages.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_working_shear_resistance_anchorages_nominal', { unit_measure: '' }), results?.outputTopAnchorages.workingShearResistanceOfTheAnchoragesNominal.toString(), getMeasurementUnit(conversionLabel.kN_lbf)],
        [t('general_results_shear_resistance_ratio', { unit_measure: '' }), results?.outputTopAnchorages.calculationForTheIntermediateAnchorges.toString(), ''],
    ];

    const intermediateChecks = [
        [t('general_results_shear_resistance_ratio'), results?.outputTopAnchorages.designCheckOfTheIntermediateAnchorages === 'Satisfied' ],
    ];

    component = <></>;

    return (
        <Document>
            <Page size='A4' style={window.localStorage.getItem("i18nextLng")=="zh-CN" ? styles.pageZh : styles.pageDefault}>
                <PDFHeader projectInfo={projectInfo} />

                <View>
                    <ProjectInformation projectInfo={projectInfo} results={results} />
                </View>

                <View>
                    <Text style={styles.title}>{t('output_table_title_input')}</Text>
                </View>

                <View style={styles.body}>
                    <View style={styles.flexColumn50}>
                        <View>
                            <Text style={styles.boldGreen}>
                                {t('input_form_accordion_rock_slope')}
                            </Text>
                            <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={rockSlopeData} />
                        </View>
                    </View>
                    <View style={styles.flexColumn50}>
                        <Image source={imgSrc}></Image>
                    </View>
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t("input_form_accordion_site_env_level")}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_value')]} data={siteEnvLevelData}/>

                  {/*   <View style={styles.mt5}>
                    {(selectedSiteEnvLevel?.coatings?.length ?? 0) > 0 && (
                            selectedSiteEnvLevel?.coatings.map((coating: string) => (
                                <Text style={styles.subtitle} key={coating}>{coating}</Text>
                            ))
                        )}
                    </View> */}
                </View>


                <View>
                    <Text style={styles.boldGreen} break>
                        {t('input_form_accordion_mesh')}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={meshData}/>
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t('input_form_accordion_crest')}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={crestData.map(row => row.map(cell => cell.toString()))} />
                </View>

                <View>
                    <Text style={styles.boldGreen} break>
                        {t('input_form_accordion_safety_coefficients')}
                    </Text>
                    <PDFTable columns={[t('output_description'), t('output_symbol'), t('output_value')]} data={safetyCoefficientsData.map(row => row.map(cell => cell.toString()))} />
                </View>

                <View>
                    <Text style={styles.title}>{t('graphical_results_general_results')}</Text>
                </View>

                <View style={styles.body}>

                    <View style={styles.resultBox}>
                        <Text style={styles.checkTitle}>
                            {t("pdf_mesh_check")}
                        </Text>
                        <Text style={{marginTop: 'auto'}}>
                            {results?.outputMeshSystemCheck.designCheckOfTheMesh === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.outputMeshSystemCheck.strengthForceRatio}  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.outputMeshSystemCheck.strengthForceRatio}  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>

                    <View style={[styles.resultBox, styles.bl0]}>
                        <Text style={styles.checkTitle}>
                            {t("pdf_top_cable_check")}
                        </Text>

                        <Text style={{marginTop: 'auto'}}>
                            {results?.outputTopCable.designCheckOfTheCable === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.outputTopCable.cableStrengthForceRatio}  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.outputTopCable.cableStrengthForceRatio}  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>
                    <View style={[styles.resultBox, styles.bl0]}>
                        <Text style={styles.checkTitle}>
                            {t("pdf_intermediate_anchorage_check")}
                        </Text>

                        <Text style={{marginTop: 'auto'}}>
                            {results?.outputTopAnchorages.designCheckOfTheIntermediateAnchorages === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.outputTopAnchorages.calculationForTheIntermediateAnchorges}  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.outputTopAnchorages.calculationForTheIntermediateAnchorges}  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>
                    <View style={[styles.resultBox, styles.bl0]}>
                        <Text style={styles.checkTitle}>
                            {t("pdf_lateral_anchorage_check")}
                        </Text>
                        <Text style={{marginTop: 'auto'}}>


                            {results?.outputTopAnchorages.designCheckOfTheLateralAnchoragesBars === 'Satisfied' ? (
                                <View>
                                    <Text style={[styles.value, styles.green]}>{results?.outputTopAnchorages.calculationsForTheLateralAnchorages}  </Text>
                                    <Image src={checkImage}></Image>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.value, styles.red]}>{results?.outputTopAnchorages.calculationsForTheLateralAnchorages}  </Text>
                                    <Image src={crossImage}></Image>
                                </View>
                            )}
                        </Text>
                    </View>

                    <View>

                    </View>
                    
                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_mesh_design')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={meshDesignData} checks={meshChecks}/>
                    </View>
                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_cable_design')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={cableDesignData} checks={cableChecks}/>
                    </View>

                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_top_anchorages')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={topAnchoragesDesignData} />
                    </View>

                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_lateral_anchorages_design')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={lateralAnchoragesDesignData} checks={lateralChecks}/>
                    </View>

                    <View>
                        <Text style={styles.boldGreen} break>
                            {t('general_results_intermediate_anchorages_design')}
                        </Text>
                        <PDFTable columns={[t('output_description'), t('output_value'), t('output_unit')]} data={intermediateAnchoragesDesignData} checks={intermediateChecks}/>
                    </View>

                </View>


                <PDFFooter />
            </Page>
        </Document>
    );
};

export default PDF;
